import { FormGroup } from '@/components/element/Form/FormGroup';
import { useQuotationTaskInvalidate } from '@/hooks/api/1480_quotation_task';
import { useQuotationTaskDocumentsInvalidate } from '@/hooks/api/1540_quotation_task_documents';
import { useCreateQuotationTaskDocument } from '@/hooks/api/1550_create_quotation_task_document';
import { useBase64FromFileInput } from '@/hooks/useBase64FromFileInput';
import { UPLOADABLE_MIME_TYPES } from '@/lib/constants';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentQuotationTask } from '@/providers/QuotationTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  task_id: number;
  name: string;
  file: string;
};

export const FileForm = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { base64Content, toBase64FromFile, fileName } = useBase64FromFileInput();
  const { mutate, data, isPending, error } = useCreateQuotationTaskDocument();
  const { invalidate } = useQuotationTaskDocumentsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    task_id: task!.id,
  });
  const { invalidate: invalidateTask } = useQuotationTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id!),
    id: task!.id,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    setValue,
    getValues,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (base64Content) setValue('file', base64Content);
    if (fileName) {
      if (getValues('name') == null || getValues('name').length == 0) {
        setValue('name', fileName);
      }
    }
  }, [base64Content]);

  useEffect(() => {
    if (data) {
      setValue('name', '');
      setValue('file', '');
      fileInputRef.current!.value = '';
      invalidate();
      invalidateTask();
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="d-flex w-100">
        <FormGroup label="ファイルを追加" required={true} classes={'me-2 flex-1'}>
          <input
            type="text"
            maxLength={50}
            className={`form-control ${errors.name && 'is-invalid'}`}
            placeholder={'名称'}
            {...register('name', { required: '入力して下さい。' })}
          />
        </FormGroup>

        <FormGroup label="　" classes={'me-2 flex-1'}>
          <input type="hidden" {...register('file', { required: '入力して下さい。' })} />
          <input
            id="document-file-input"
            type="file"
            className={`form-control ${errors.file && 'is-invalid'}`}
            onChange={toBase64FromFile}
            ref={fileInputRef}
            accept={UPLOADABLE_MIME_TYPES.join(',')}
          />
        </FormGroup>

        <div className="form-group flex-shrink-0">
          <label>&nbsp;</label>
          <button className="btn w-100 btn-icon bg-gradient-dark">
            <i className="fa fa-plus"></i>追加
          </button>
        </div>
      </div>
    </form>
  );
};
